import {
  organizationRegionNames,
  publicPrimaryLeader,
  type PublicPrimaryLeader,
} from "@shdeed/foundation";

export type OrganizationProfileEditorInput = {
  alternateName?: string;
  leaderPublished?: boolean;
  leaderDisplayName?: string;
  leaderTitle?: string;
  leaderPhotoUrl?: string;
  leaderShortBio?: string;
  addressLine1: string;
  addressLine2: string;
  brandColor: string;
  category: "Islamic center" | "Community center";
  city: string;
  connectionPolicy: "open" | "approval_required";
  country: string;
  displayName: string;
  logoStoragePath: string;
  postalCode: string;
  publicEmail: string;
  publicPhone: string;
  publicVisibilityDirectory: boolean;
  publicVisibilityDiscovery: boolean;
  publicVisibilityProfile: boolean;
  region: string;
  timeZone: string;
  websiteUrl: string;
  welcomeHeadline: string;
  welcomeHeadlineAr: string;
  welcomeSummary: string;
  welcomeSummaryAr: string;
};

export type OrganizationProfileSelectOption = {
  value: string;
  label: string;
};

export type OrganizationProfileEditorDraft = Omit<
  OrganizationProfileEditorInput,
  "addressLine1" | "addressLine2" | "logoStoragePath" | "publicEmail" | "publicPhone" | "websiteUrl"
> & {
  addressLine1: string | null;
  addressLine2: string | null;
  logoStoragePath: string | null;
  openConnection: boolean;
  publicEmail: string | null;
  publicPhone: string | null;
  source: "experience_nineteen";
  status: "requested";
  websiteUrl: string | null;
};

export type OrganizationProfileEditorResult =
  | {
      ok: true;
      draft: OrganizationProfileEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type OrganizationProfileEditorFixture = {
  alternateName?: string;
  primaryLeader?: PublicPrimaryLeader | null;
  brandColor: string;
  category: "Islamic center" | "Community center";
  city: string;
  connectionPolicy: string;
  displayName: string;
  branding?: {
    logoStoragePath?: string | null;
  };
  address?: {
    city?: string;
    line1?: string | null;
    line2?: string | null;
    postalCode?: string;
    region?: string;
    country?: string;
  };
  publicContact?: {
    email?: string | null;
    phone?: string | null;
    websiteUrl?: string | null;
  };
  publicVisibility?: {
    directory?: boolean;
    discovery?: boolean;
    profile?: boolean;
  };
  timeZone?: string;
  welcomeHeadline: string;
  welcomeHeadlineAr?: string;
  welcomeSummary: string;
  welcomeSummaryAr?: string;
};

const nameLimit = 120;
const addressLineLimit = 120;
const cityLimit = 120;
const regionLimit = 80;
const postalCodeLimit = 24;
const timeZoneLimit = 80;
const publicEmailLimit = 254;
const publicPhoneLimit = 32;
const storagePathLimit = 512;
const websiteUrlLimit = 2048;
const headlineLimit = 180;
const summaryLimit = 600;

export const organizationCountryOptions: readonly OrganizationProfileSelectOption[] = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
];

export const organizationRegionOptions = organizationRegionNames;

export const organizationTimeZoneOptions: readonly OrganizationProfileSelectOption[] = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Phoenix", label: "Arizona Time" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
  { value: "America/Toronto", label: "Eastern Time - Canada" },
  { value: "America/Winnipeg", label: "Central Time - Canada" },
  { value: "America/Edmonton", label: "Mountain Time - Canada" },
  { value: "America/Vancouver", label: "Pacific Time - Canada" },
  { value: "America/Halifax", label: "Atlantic Time - Canada" },
  { value: "America/St_Johns", label: "Newfoundland Time" },
];

export function buildOrganizationProfileEditorInputFromFixture(
  fixture: OrganizationProfileEditorFixture,
): OrganizationProfileEditorInput {
  return {
    alternateName: fixture.alternateName ?? "",
    leaderPublished: fixture.primaryLeader?.isPublished === true,
    leaderDisplayName: fixture.primaryLeader?.displayName ?? "",
    leaderTitle: fixture.primaryLeader?.title ?? "",
    leaderPhotoUrl: fixture.primaryLeader?.photoUrl ?? "",
    leaderShortBio: fixture.primaryLeader?.shortBio ?? "",
    addressLine1: fixture.address?.line1 ?? "",
    addressLine2: fixture.address?.line2 ?? "",
    brandColor: fixture.brandColor,
    category: fixture.category,
    city: fixture.address?.city ?? fixture.city,
    connectionPolicy:
      fixture.connectionPolicy === "approval_required" ? "approval_required" : "open",
    country: fixture.address?.country ?? "US",
    displayName: fixture.displayName,
    logoStoragePath: fixture.branding?.logoStoragePath ?? "",
    postalCode: fixture.address?.postalCode ?? "",
    publicEmail: fixture.publicContact?.email ?? "",
    publicPhone: fixture.publicContact?.phone ?? "",
    publicVisibilityDirectory: fixture.publicVisibility?.directory ?? true,
    publicVisibilityDiscovery: fixture.publicVisibility?.discovery ?? true,
    publicVisibilityProfile: fixture.publicVisibility?.profile ?? true,
    region: fixture.address?.region ?? "",
    timeZone: fixture.timeZone ?? "America/New_York",
    websiteUrl: fixture.publicContact?.websiteUrl ?? "",
    welcomeHeadline: fixture.welcomeHeadline,
    welcomeHeadlineAr: fixture.welcomeHeadlineAr ?? "",
    welcomeSummary: fixture.welcomeSummary,
    welcomeSummaryAr: fixture.welcomeSummaryAr ?? "",
  };
}

export function buildDefaultOrganizationProfileEditorInput(): OrganizationProfileEditorInput {
  return {
    addressLine1: "",
    addressLine2: "",
    brandColor: "#116466",
    category: "Islamic center",
    city: "Huntingdon Valley",
    connectionPolicy: "open",
    country: "US",
    displayName: "Community Organization",
    logoStoragePath: "",
    postalCode: "19006",
    publicEmail: "",
    publicPhone: "",
    publicVisibilityDirectory: true,
    publicVisibilityDiscovery: true,
    publicVisibilityProfile: true,
    region: "PA",
    timeZone: "America/New_York",
    websiteUrl: "",
    welcomeHeadline: "A community home for prayer, learning, and service.",
    welcomeHeadlineAr: "بيت مجتمعي للصلاة والتعلم والخدمة.",
    welcomeSummary: "Follow community updates, services, and real-time Shdeed workflows.",
    welcomeSummaryAr: "تابع تحديثات المجتمع والخدمات ومسارات شديد المباشرة.",
  };
}

export function buildEmptyOrganizationProfileEditorInput(): OrganizationProfileEditorInput {
  return {
    addressLine1: "",
    addressLine2: "",
    brandColor: "",
    category: "Islamic center",
    city: "",
    connectionPolicy: "open",
    country: "",
    displayName: "",
    logoStoragePath: "",
    postalCode: "",
    publicEmail: "",
    publicPhone: "",
    publicVisibilityDirectory: true,
    publicVisibilityDiscovery: true,
    publicVisibilityProfile: true,
    region: "",
    timeZone: "",
    websiteUrl: "",
    welcomeHeadline: "",
    welcomeHeadlineAr: "",
    welcomeSummary: "",
    welcomeSummaryAr: "",
  };
}

export function evaluateOrganizationProfileEditorInput(
  input: OrganizationProfileEditorInput,
): OrganizationProfileEditorResult {
  const addressLine1 = nullableText(input.addressLine1);
  const addressLine2 = nullableText(input.addressLine2);
  const displayName = input.displayName.trim();
  const city = input.city.trim();
  const country = input.country.trim().toUpperCase();
  const brandColor = input.brandColor.trim();
  const logoStoragePath = nullableText(input.logoStoragePath);
  const postalCode = input.postalCode.trim().toUpperCase();
  const publicEmail = nullableText(input.publicEmail);
  const rawPublicPhone = nullableText(input.publicPhone);
  const publicPhone = rawPublicPhone ? formatOrganizationProfilePhone(rawPublicPhone) : null;
  const region = input.region.trim().toUpperCase();
  const timeZone = input.timeZone.trim();
  const websiteUrl = nullableText(input.websiteUrl);
  const welcomeHeadline = input.welcomeHeadline.trim();
  const welcomeHeadlineAr = input.welcomeHeadlineAr.trim();
  const welcomeSummary = input.welcomeSummary.trim();
  const welcomeSummaryAr = input.welcomeSummaryAr.trim();
  const errors: string[] = [];
  if ((input.alternateName?.length ?? 0) > 120)
    errors.push("Alternate name must be at most 120 characters.");
  if (input.leaderPublished && !organizationPublicLeaderFromEditor(input))
    errors.push(
      "Public leader needs a name and title (120 characters maximum), an optional HTTPS photo and a bio of at most 600 characters.",
    );

  validateRequiredText("Display name", displayName, nameLimit, errors);
  validateOptionalText("Address line 1", addressLine1, addressLineLimit, errors);
  validateOptionalText("Address line 2", addressLine2, addressLineLimit, errors);
  validateRequiredText("City", city, cityLimit, errors);
  validateRequiredText("Region", region, regionLimit, errors);
  validateRequiredText("Postal code", postalCode, postalCodeLimit, errors);
  validateRequiredText("Timezone", timeZone, timeZoneLimit, errors);
  validateOptionalText("Logo storage path", logoStoragePath, storagePathLimit, errors);
  validateRequiredText("English headline", welcomeHeadline, headlineLimit, errors);
  validateOptionalText("Arabic headline", welcomeHeadlineAr, headlineLimit, errors);
  validateRequiredText("English summary", welcomeSummary, summaryLimit, errors);
  validateOptionalText("Arabic summary", welcomeSummaryAr, summaryLimit, errors);
  validateOptionalText("Public email", publicEmail, publicEmailLimit, errors);
  validateOptionalText("Public phone", publicPhone, publicPhoneLimit, errors);
  validateOptionalText("Website URL", websiteUrl, websiteUrlLimit, errors);

  if (!/^#[0-9a-fA-F]{6}$/.test(brandColor)) {
    errors.push("Brand color must be a 6-digit hex color.");
  }

  if (!/^[A-Z]{2}$/.test(country)) {
    errors.push("Country must be a two-letter ISO country code.");
  } else if (!isSupportedOption(organizationCountryOptions, country)) {
    errors.push("Country must be selected from the supported country list.");
  }

  if (!isSupportedOption(organizationRegionOptions, region)) {
    errors.push("State or province must be selected from the supported list.");
  }

  if (!isSupportedOption(organizationTimeZoneOptions, timeZone)) {
    errors.push("Timezone must be selected from the supported timezone list.");
  }

  if (country === "US" && !/^[0-9]{5}(-[0-9]{4})?$/.test(postalCode)) {
    errors.push("US postal code must be 12345 or 12345-6789.");
  }

  if (country === "CA" && !/^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]$/.test(postalCode)) {
    errors.push("Canadian postal code must be A1A 1A1.");
  }

  if (publicEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(publicEmail)) {
    errors.push("Public email must be a valid email address.");
  }

  if (rawPublicPhone && !isFormattedOrganizationPhone(publicPhone)) {
    errors.push("Public phone must use (111)111-9999 format.");
  }

  if (websiteUrl && !/^https?:\/\/\S+$/i.test(websiteUrl)) {
    errors.push("Website URL must start with http:// or https://.");
  }

  if (logoStoragePath && !isSafeOrganizationBrandingStoragePath(logoStoragePath)) {
    errors.push("Logo storage path must be under an organization branding path.");
  }

  if (input.category !== "Islamic center" && input.category !== "Community center") {
    errors.push("Category must be a supported organization category.");
  }

  if (input.connectionPolicy !== "open" && input.connectionPolicy !== "approval_required") {
    errors.push("Connection policy must be open or approval required.");
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    draft: {
      alternateName: input.alternateName?.trim() ?? "",
      leaderPublished: input.leaderPublished === true,
      leaderDisplayName: input.leaderDisplayName?.trim() ?? "",
      leaderTitle: input.leaderTitle?.trim() ?? "",
      leaderPhotoUrl: input.leaderPhotoUrl?.trim() ?? "",
      leaderShortBio: input.leaderShortBio?.trim() ?? "",
      addressLine1,
      addressLine2,
      brandColor,
      category: input.category,
      city,
      connectionPolicy: input.connectionPolicy,
      country,
      displayName,
      logoStoragePath,
      openConnection: input.connectionPolicy === "open",
      postalCode,
      publicEmail,
      publicPhone,
      publicVisibilityDirectory: input.publicVisibilityDirectory,
      publicVisibilityDiscovery: input.publicVisibilityDiscovery,
      publicVisibilityProfile: input.publicVisibilityProfile,
      region,
      source: "experience_nineteen",
      status: "requested",
      timeZone,
      websiteUrl,
      welcomeHeadline,
      welcomeHeadlineAr,
      welcomeSummary,
      welcomeSummaryAr,
    },
  };
}

export function organizationPublicLeaderFromEditor(
  input: Pick<
    OrganizationProfileEditorInput,
    "leaderPublished" | "leaderDisplayName" | "leaderTitle" | "leaderPhotoUrl" | "leaderShortBio"
  >,
): PublicPrimaryLeader | null {
  return publicPrimaryLeader({
    isPublished: input.leaderPublished === true,
    displayName: input.leaderDisplayName?.trim(),
    title: input.leaderTitle?.trim(),
    photoUrl: input.leaderPhotoUrl?.trim() || null,
    shortBio: input.leaderShortBio?.trim() ?? "",
  });
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: string | null,
  maxLength: number,
  errors: string[],
): void {
  if (value !== null && value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function nullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function formatOrganizationProfilePhone(value: string): string {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/gu, "");
  const nationalDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (nationalDigits.length === 10) {
    return `(${nationalDigits.slice(0, 3)})${nationalDigits.slice(3, 6)}-${nationalDigits.slice(6)}`;
  }

  return trimmed;
}

function isFormattedOrganizationPhone(value: string | null): boolean {
  return value === null || /^\([0-9]{3}\)[0-9]{3}-[0-9]{4}$/.test(value);
}

function isSupportedOption(
  options: readonly OrganizationProfileSelectOption[],
  value: string,
): boolean {
  return options.some((option) => option.value === value);
}

function isSafeOrganizationBrandingStoragePath(value: string): boolean {
  return (
    /^organizations\/[a-z0-9][a-z0-9_-]{0,99}\/branding\/[^/].*/.test(value) &&
    !value.endsWith("/") &&
    !value.includes("..")
  );
}
